// Copyright (c) 2021 WiseTime. All rights reserved.

import React, { useRef, useState } from "react"
import { useKeyPressEvent } from "react-use"
import { autoUpdate, flip, limitShift, Placement, shift, size, useFloating } from "@floating-ui/react-dom"
import SearchDropdownContent from "./SearchDropdownContent"
import { DropdownHeader, DropdownHeaderIcon, Wrapper } from "./styles"
import { Destination, ListItemFavouriteType, ListItemType } from "./types"
import { useDropdownHandling } from "./useDropdownHandling"
import { ViewMode } from "../../tags/slice"

type Props = {
  placement: Placement,
  timeGroupsIds: Destination,
  timeRowsIds: Destination,
  addedItemIds: string[],
  icon: string,
  searchSortDropdown: React.ReactElement,
  recentSortDropdown: React.ReactElement,
  title: string,
  placeholder: string,
  favouritesList: ListItemFavouriteType[],
  recentList: ListItemType[],
  searchResultList: ListItemType[],
  viewMode: ViewMode,
  isSearching: boolean,
  isTimeRow: boolean,
  isFocusable: boolean,
  orderBy: number,
  className?: string,
  showFavouriteButton: boolean,
  stopPropagation: boolean,
  onSearchItems: (searchText: string) => void,
  onResetSearchItems: () => void,
  onAddItemToDestination: (refId: string, timeGroupsIds: Destination, timeRowsIds: Destination) => void,
  // event param will be used in parent component callbacks for calling event.stopPropagation()
  // to avoid conflict with wrapper click(onAddItemToDestination)
  onFavouriteItem: (event: React.MouseEvent<HTMLButtonElement>, refId: string) => void,
  onUnfavouriteItem: (event: React.MouseEvent<HTMLButtonElement>, id: string) => void,
  onChangeViewMode: (value: ViewMode) => void,
  onReorderDraggableItem: (id: string, newIndex: number) => void,
  // site dependent so should be used in pure function or in useCallback that is siteId dependent
  onFetchItems: () => void,
  onFocus?: () => void,
}

const SearchDropdown = ({
  placement,
  timeGroupsIds,
  timeRowsIds,
  addedItemIds,
  icon,
  searchSortDropdown,
  recentSortDropdown,
  title,
  placeholder,
  favouritesList,
  recentList,
  searchResultList,
  viewMode,
  isSearching,
  orderBy,
  isTimeRow,
  isFocusable,
  className,
  stopPropagation,
  onSearchItems,
  onResetSearchItems,
  onAddItemToDestination,
  onFavouriteItem,
  onUnfavouriteItem,
  onChangeViewMode,
  onReorderDraggableItem,
  onFetchItems,
  showFavouriteButton,
  onFocus,
}: Props): React.ReactElement<Props> => {
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const headerRef = useRef<HTMLButtonElement | null>(null)
  const [isFocused, setIsFocused] = useState(false)

  const {
    isDropdownVisible,
    setIsDropdownVisible,
  } = useDropdownHandling(dropdownRef, isTimeRow)
  const {
    refs,
    floatingStyles,
  } = useFloating({
    strategy: "fixed",
    placement,
    middleware: [
      size({
        apply({ availableHeight, elements }) {
          const values = [availableHeight - 20, window.innerHeight - 40]
          elements.floating.style.height = `${Math.min(...values)}px`
        },
      }),
      flip({ fallbackStrategy: "initialPlacement" }),
      shift({ limiter: limitShift() }),
    ],
    whileElementsMounted: (reference, floating, update) =>
      autoUpdate(reference, floating, update, {
        animationFrame: true,
      }),
  })
  
  const handleClick = (): void => {
    if (!isDropdownVisible) {
      onFetchItems()
    }
    setIsDropdownVisible(!isDropdownVisible)
  }
  
  useKeyPressEvent("Enter", () => {
    if (isFocusable && isFocused && !isDropdownVisible) {
      handleClick()
    }
  })
  
  useKeyPressEvent("Escape", () => {
    if (isFocusable && isFocused) {
      headerRef.current?.blur()
    }
  })

  return (
    <Wrapper
      className={className}
      ref={dropdownRef}
      onClick={(event: React.MouseEvent<HTMLDivElement>): void => {
        stopPropagation && event.stopPropagation()
      }}
    >
      <DropdownHeader
        ref={ref => {
          headerRef.current = ref
          refs.setReference(ref)
        }}
        className={isDropdownVisible ? "is-active" : ""}
        onClick={() => {
          if (!isFocusable) {
            handleClick()
            return
          }
          const isSafari = /apple/i.test(navigator.vendor)
          const majorVersion = isSafari ?
            navigator.userAgent.split(" ").find(item => item.startsWith("Version/"))
              ?.split(/[/.]/)[1] : undefined
          const sonomaSafariVersion = 17
          if (Number(majorVersion) < sonomaSafariVersion) {
            headerRef.current?.focus()
          }
        }}
        onMouseDown={() => isFocusable && headerRef.current?.blur()}
        onMouseEnter={() => !isDropdownVisible && onFetchItems()}
        isDropdownVisible={isDropdownVisible}
        title={title}
        onFocus={() => {
          if (!isFocusable) {
            return
          }
          setIsFocused(true)
          onFocus && onFocus()
          handleClick()
        }}
        onBlur={() => {
          if (!isFocusable) {
            return
          }
          setIsFocused(false)
        }}
        tabIndex={isFocusable ? 0 : -1}
      >
        <DropdownHeaderIcon name={icon}/>
      </DropdownHeader>
      {isDropdownVisible && (
        <SearchDropdownContent
          ref={refs.setFloating}
          style={floatingStyles}
          headerRef={headerRef.current}
          setIsDropdownVisible={setIsDropdownVisible}
          timeGroupsIds={timeGroupsIds}
          timeRowsIds={timeRowsIds}
          addedItemIds={addedItemIds}
          searchSortDropdown={searchSortDropdown}
          recentSortDropdown={recentSortDropdown}
          placeholder={placeholder}
          favouritesList={favouritesList}
          recentList={recentList}
          searchResultList={searchResultList}
          viewMode={viewMode}
          isSearching={isSearching}
          orderBy={orderBy}
          isFocusable={isFocusable}
          showFavouriteButton={showFavouriteButton}
          onSearchItems={onSearchItems}
          onResetSearchItems={onResetSearchItems}
          onAddItemToDestination={onAddItemToDestination}
          onFavouriteItem={onFavouriteItem}
          onUnfavouriteItem={onUnfavouriteItem}
          onChangeViewMode={onChangeViewMode}
          onReorderDraggableItem={onReorderDraggableItem}
        />
      )}
    </Wrapper>
  )
}

export default SearchDropdown
